import { EmployeeStatusEnum } from '@electricjs/core_entity-client';

import { Employee } from '@/types/employees';
import { GetOrganizationEmployeesArgs } from '@/types/organizations';

export type DerivableEmployeesArgs = Partial<
  Omit<GetOrganizationEmployeesArgs, 'organizationId'>
>;

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const ARGS_DERIVED_HERE = new Set<keyof DerivableEmployeesArgs>([
  'status',
  'term',
  'startDateFrom',
  'startDateTo',
  'terminated',
  'organizationPackageId',
  'deviceNotRequired',
  'limit',
  'offset',
]);

// The backend reads a date-only bound as midnight UTC: core-entity sets no
// session time zone and the database server runs in UTC (log_timezone).
// A date that doesn't exist is undefined: Date.parse gives NaN for 2024-13-45
// and rolls 2024-02-30 over to March, while the backend rejects both.
const parseDateOnly = (value: string): number | undefined => {
  if (!DATE_ONLY.test(value)) {
    return undefined;
  }
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().startsWith(value)
    ? time
    : undefined;
};

const isPageNumber = (value: number | undefined) =>
  value === undefined || (Number.isInteger(value) && value >= 0);

/**
 * Applies the endpoint's own rules to the full cached list, so a caller that
 * filters or pages gets what the server would have returned, in the same order.
 * The rules mirror core-entity's employees_repository filters and
 * employees_service.search_employees.
 *
 * Returns undefined when the server has to answer instead:
 * - an argument this can't apply: any sort (the database orders text with the
 *   en_US.UTF-8 collation, which JavaScript can't reproduce exactly),
 *   `xboardingStatus`, `hasAssets`, `hasDevices`, `groupIds`, `includeDevices`
 * - a date that isn't a real YYYY-MM-DD, only one of `limit` and `offset`, or
 *   a negative or fractional one (the backend rejects all of these)
 *
 * Known gap: the backend lowercases `term` with Python's casefold, JavaScript's
 * toLowerCase differs only for characters like "ß".
 */
export const deriveFilteredEmployees = (
  employees: Employee[],
  args: DerivableEmployeesArgs
): { results: Employee[]; total: number } | undefined => {
  const needsServer = (
    Object.keys(args) as Array<keyof DerivableEmployeesArgs>
  ).some(key => args[key] !== undefined && !ARGS_DERIVED_HERE.has(key));
  if (needsServer) {
    return undefined;
  }

  const {
    status,
    term,
    startDateFrom,
    startDateTo,
    terminated,
    organizationPackageId,
    deviceNotRequired,
    limit,
    offset,
  } = args;

  const requestedStatuses = [status ?? []].flat();
  const statuses: EmployeeStatusEnum[] =
    requestedStatuses.length > 0
      ? requestedStatuses
      : [EmployeeStatusEnum.Active];
  if (
    (limit === undefined) !== (offset === undefined) ||
    !isPageNumber(limit) ||
    !isPageNumber(offset)
  ) {
    return undefined;
  }

  const from =
    startDateFrom === undefined ? undefined : parseDateOnly(startDateFrom);
  const to = startDateTo === undefined ? undefined : parseDateOnly(startDateTo);
  if (
    (startDateFrom !== undefined && from === undefined) ||
    (startDateTo !== undefined && to === undefined)
  ) {
    return undefined;
  }

  // Splitting on a single space keeps empty pieces, and an empty piece matches
  // everything, same as the backend.
  const termPieces = term?.toLowerCase().split(' ');

  const filtered = employees.filter(employee => {
    if (!statuses.includes(employee.status)) {
      return false;
    }
    if (terminated !== undefined && !!employee.terminationDate !== terminated) {
      return false;
    }
    if (
      organizationPackageId !== undefined &&
      employee.organizationPackageId !== organizationPackageId
    ) {
      return false;
    }
    if (
      deviceNotRequired !== undefined &&
      (employee.deviceNotRequired ?? false) !== deviceNotRequired
    ) {
      return false;
    }
    if (from !== undefined || to !== undefined) {
      // A null start date fails any comparison in SQL, so it never matches.
      const startedAt = employee.startDate
        ? Date.parse(employee.startDate)
        : NaN;
      if (Number.isNaN(startedAt)) {
        return false;
      }
      if (from !== undefined && startedAt < from) {
        return false;
      }
      if (to !== undefined && startedAt >= to + DAY_IN_MS) {
        return false;
      }
    }
    if (termPieces) {
      const details = [
        employee.firstName,
        employee.lastName,
        employee.id,
        employee.preferredFirstName,
        employee.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!termPieces.every(piece => details.includes(piece))) {
        return false;
      }
    }
    return true;
  });

  return {
    results:
      limit === undefined || offset === undefined
        ? filtered
        : filtered.slice(offset, offset + limit),
    total: filtered.length,
  };
};
